import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { Bell, BellRing, Radio } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";

type BrowserPermission = NotificationPermission | "unsupported";

type PushEngageWindow = Window & { PushEngage?: unknown[] };

const SEEN_ALERTS_KEY = "kora-arabia-seen-alerts-v1";
const ALERT_EXCLUDED_PATHS = new Set(["/cms", "/privacy", "/terms", "/contact", "/404"]);

function getSeenAlerts() {
  if (typeof window === "undefined") return new Set<string>();
  try {
    const value = JSON.parse(window.localStorage.getItem(SEEN_ALERTS_KEY) ?? "[]");
    return new Set(Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : []);
  } catch {
    return new Set<string>();
  }
}

function saveSeenAlerts(ids: string[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(SEEN_ALERTS_KEY, JSON.stringify(ids.slice(-50)));
}

async function showBrowserAlert(title: string, body: string, tag: string, url: string) {
  if (typeof window === "undefined" || !("Notification" in window) || window.Notification.permission !== "granted") return;
  if ("serviceWorker" in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification(title, { body, tag, data: { url }, dir: "rtl", lang: "ar" });
      return;
    } catch {
      // Fall back to the regular notification API when registration is unavailable.
    }
  }
  const notification = new window.Notification(title, { body, tag });
  notification.onclick = () => { window.focus(); window.location.assign(url); notification.close(); };
}

export function getAlertIds(data: { liveStreams?: Array<{ id: number }>; recentHighlights?: Array<{ id: number }> } | undefined) {
  return [
    ...(data?.liveStreams ?? []).map((item) => `live-${item.id}`),
    ...(data?.recentHighlights ?? []).map((item) => `highlight-${item.id}`),
  ];
}

export default function SiteAlerts() {
  const alerts = trpc.sports.alerts.useQuery(undefined, {
    retry: false,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 30_000,
  });
  const [location] = useLocation();
  const [permission, setPermission] = useState<BrowserPermission>("unsupported");
  const [ready, setReady] = useState(false);
  const ids = useMemo(() => getAlertIds(alerts.data), [alerts.data]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setPermission("Notification" in window ? window.Notification.permission : "unsupported");
  }, []);

  useEffect(() => {
    if (!alerts.data) return;
    const seen = getSeenAlerts();
    if (!ready) {
      saveSeenAlerts(ids);
      setReady(true);
      return;
    }
    const freshLive = (alerts.data.liveStreams ?? []).filter((item) => !seen.has(`live-${item.id}`));
    const freshHighlights = (alerts.data.recentHighlights ?? []).filter((item) => !seen.has(`highlight-${item.id}`));
    for (const item of freshLive) {
      const title = "بدأ بث رسمي الآن";
      const body = `${item.homeTeam} × ${item.awayTeam}`;
      const url = `/matches#match-${item.id}`;
      toast(title, { description: body, icon: <Radio size={16} />, duration: 8_000, action: { label: "فتح المباراة", onClick: () => window.location.assign(url) } });
      if (permission === "granted") void showBrowserAlert(title, body, `live-${item.id}`, url);
    }
    for (const item of freshHighlights) {
      const title = "وصل ملخص جديد";
      const body = item.match ? `${item.match.homeTeam} × ${item.match.awayTeam}` : item.title;
      const url = `/highlights#highlight-${item.id}`;
      toast(title, { description: body, icon: <BellRing size={16} />, duration: 8_000, action: { label: "فتح الملخص", onClick: () => window.location.assign(url) } });
      if (permission === "granted") void showBrowserAlert(title, body, `highlight-${item.id}`, url);
    }
    saveSeenAlerts([...Array.from(seen), ...ids]);
  }, [alerts.data, ids, permission, ready]);

  const enableNotifications = async () => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      toast("الإشعارات غير مدعومة", { description: "يمكنك متابعة التنبيهات داخل الموقع." });
      return;
    }
    const pushEngage = (window as PushEngageWindow).PushEngage;
    if (Array.isArray(pushEngage)) {
      pushEngage.push((sdk: { showNativePermissionPrompt?: () => Promise<unknown> }) => {
        const result = sdk.showNativePermissionPrompt?.();
        if (result) void result.then(() => toast.success("تم طلب تفعيل إشعارات كورة أرابيا"));
      });
      return;
    }
    const result = await window.Notification.requestPermission();
    setPermission(result);
    if (result === "granted") toast.success("تم تفعيل إشعارات كورة أرابيا");
  };

  if (permission !== "default" || ALERT_EXCLUDED_PATHS.has(location) || location.startsWith("/cms/")) return null;
  return <button type="button" onClick={enableNotifications} className="fixed bottom-4 left-4 z-40 inline-flex items-center gap-2 rounded-xl border border-amber-200/30 bg-[#102b45]/95 px-3 py-2 text-xs font-bold text-amber-100 shadow-xl backdrop-blur" aria-label="تفعيل إشعارات البث والملخصات"><Bell size={15} /> تفعيل التنبيهات</button>;
}
